import React, {memo, useMemo} from 'react'
import cx from 'classnames'

/* Components */
import PriceSkeleton from './PriceSkeleton'

/* Utils */
import {formatPrice} from '../../../../modules/product/productMobileUtils'
import DataSet from '../../../../utils/DataSet'

/* Constants */
import {DAVINCI_EVENT_NAMES} from '../../../../modules/analytics/davinci/davinciConstants'

/* Styles */
import s from './price.scss'

const Price = memo((
  {
    className,
    product,
    campaignEvaluateResult,
    sku,
    pageTypeForDavinci,
    productId,
    listingId,
    merchantId,
    merchantName,
    categoryId,
    categoryName,
    price,
    isGetCampaignsPdpDashboardPending,
    isBot,
    isPremium,
    loyaltyInfoPending
  }) => {
  const checkoutPrice = campaignEvaluateResult?.evaluateResult?.discountedAmount
  const premiumPrice = campaignEvaluateResult?.evaluateAsPremiumResult?.discountedAmount
  const getPrice = () => {
    let priceMap = {
      currency: 'TL'
    }

    if (product?.prices?.length > 1) {
      priceMap.currentPrice = product?.prices?.[1]?.formattedPrice
      priceMap.currentPriceValue = product?.prices?.[1]?.value
      priceMap.currentPriceDiscountRate = product?.prices?.[1]?.discountRate
      priceMap.prevPrice = product?.prices?.[0]?.formattedPrice
      priceMap.prevPriceValue = product?.prices?.[0]?.value
      priceMap.discountText =
        <div className={s.discount} data-test-id={'discount-text'}>
          <i className={s.discountTextIcon}/>
          <span className={s.discountText}>{'30 günün en düşük fiyatı'}</span>
        </div>
    }

    if (product?.prices?.length === 1) {
      priceMap.currentPrice = product?.prices?.[0]?.formattedPrice
      priceMap.currentPriceValue = product?.prices?.[0]?.value
    }

    if (product?.unitPrice?.value > 0) {
      priceMap.unitPriceText = `(${product.unitPrice.value} TL/adet)`
    }

    return priceMap
  }

  // eslint-disable-next-line consistent-return
  const getResult = useMemo(() => {
    const checkoutPrice = campaignEvaluateResult?.evaluateResult?.discountedAmount;
    const premiumPrice = campaignEvaluateResult?.evaluateAsPremiumResult?.discountedAmount;
    const showEarn = Boolean(checkoutPrice || premiumPrice || getPrice().prevPrice);

    const defaultResult = {
      showCheckoutPrice: false,
      showPremiumPrice: false,
      showNonPremiumPrice: false,
      showDefaultPrice: true,
      showEarn
    };

    if (premiumPrice > 0) {
      if (isPremium) {
        return {...defaultResult, showPremiumPrice: true, showDefaultPrice: false};
      } else {
        return {...defaultResult, showNonPremiumPrice: true, showDefaultPrice: false};
      }
    } else if (checkoutPrice > 0) {
      return {...defaultResult, showCheckoutPrice: true, showDefaultPrice: false};
    }

    return defaultResult;
  }, [campaignEvaluateResult, isPremium, loyaltyInfoPending, isGetCampaignsPdpDashboardPending])

  const calculateEarningPrice = () => {
    let total = 0
    let finalPriceOnSale = campaignEvaluateResult?.evaluateAsPremiumResult?.finalPriceOnSale || campaignEvaluateResult?.evaluateResult?.finalPriceOnSale || getPrice()?.prevPriceValue
    let discountedAmount = campaignEvaluateResult?.evaluateAsPremiumResult?.discountedAmount || campaignEvaluateResult?.evaluateResult?.discountedAmount || getPrice()?.currentPriceValue

    // eslint-disable-next-line
    finalPriceOnSale && (total += (finalPriceOnSale - discountedAmount) || 0)

    return total
  }

  const handleSeeEarnings = () => {
    window?.hepsiBus?.track(DAVINCI_EVENT_NAMES.LINK_CLICK, {
      location: 'buybox',
      position: '',
      url: '',
      page_type: pageTypeForDavinci,
      text: 'Kazancımı gör',
      placement: 'button',
      page_value: sku
    })
  }

  const renderSeeEarnings = () => {
    return (
      <div
        onMouseEnter={() => handleSeeEarnings()}
        className={s.seeEarningsWrapper}
        data-hbus={DataSet.createComponentViewDataForDavinci({
          pageType: pageTypeForDavinci,
          pageValue: sku,
          placement: 'buybox',
          component: 'Kazancımı gör',
          componentDetail: `Kazancımı gör ${formatPrice(calculateEarningPrice())}`,
          categoryHierarchy: `${categoryId} - ${categoryName}`,
          productId,
          listingId,
          merchantId,
          merchantName,
          price: `${price}`
        })}>
        <div className={s.seeEarnings} data-test-id='see-earnings'>
          <span className={s.text}>{'Kazancımı gör'}</span>
          <span className={s.amountText}>{formatPrice(calculateEarningPrice(), true)}</span>
          <span className={s.line}></span>
          <i className={s.icon}></i>
        </div>
        <div className={s.tooltipText} data-test-id='see-earnings-tooltip'>
          {
            <div className={cx(s.content)}>
              <div className={s.header}>
                <span>{'Fiyat Detayı'}</span>
              </div>
              <div className={s.priceContent}>
                <div className={s.priceTooltip}>
                  <span className={s.text}>{'Fiyat'}</span>
                  <span
                    className={s.amountBase}>{`${formatPrice(campaignEvaluateResult?.evaluateAsPremiumResult?.finalPriceOnSale || campaignEvaluateResult?.evaluateResult?.finalPriceOnSale || getPrice()?.prevPriceValue, true)}`}</span>
                </div>
                {
                  getPrice().prevPrice && !campaignEvaluateResult?.evaluateAsPremiumResult?.finalPriceOnSale &&
                  <div className={cx(s.priceTooltip, s.discount)}>
                    <span className={s.text}>{`İndirim %${getPrice()?.currentPriceDiscountRate}`}</span>
                    <span
                      className={s.amount}>{`${formatPrice(getPrice().prevPriceValue - getPrice().currentPriceValue, true)}`}</span>
                  </div>
                }
                {
                  !campaignEvaluateResult?.evaluateAsPremiumResult && campaignEvaluateResult?.evaluateResult?.campaigns?.map((item) => (
                    <div className={cx(s.priceTooltip, s.discount)}>
                      <span className={s.text}>{item?.name}</span>
                      <span className={s.amount}>{`${formatPrice(item?.totalDiscount, true)}`}</span>
                    </div>
                  ))
                }
                {
                  campaignEvaluateResult?.evaluateAsPremiumResult?.campaigns?.map((item) => (
                    <div className={cx(s.priceTooltip, s.discount)}>
                      {
                        item?.isPremiumCampaign ?
                          <>
                            <div className={cx(s.textArea, s.premium)}>
                              <span className={s.text}>{'Premium’a özel indirim'}</span>
                            </div>
                            <span className={cx(s.amount, s.premium)}>{formatPrice(item?.totalDiscount, true)}</span>
                          </> :
                          <>
                            <span className={s.text}>{item?.name}</span>
                            <span className={s.amount}>{`${formatPrice(item?.totalDiscount, true)}`}</span>
                          </>
                      }
                    </div>
                  ))
                }
                <div className={s.dividers}/>
                <div className={cx(s.priceTooltip)}>
                  <div className={s.textArea}>
                    <span>🥳</span>
                    <span className={s.totalEarnText}>{'Bu üründe toplam kazanç'}</span>
                  </div>
                  <span
                    className={s.totalPrice}>{formatPrice(calculateEarningPrice(), true)}</span>
                </div>
                <div className={s.dividers}/>
              </div>
            </div>
          }
        </div>
      </div>
    )
  }

  const renderPrevPrice = (
    {
      lineThrough = false,
      showDiscount = false,
      discountRate = 0
    }) => {
    if (getResult?.showNonPremiumPrice || (!premiumPrice && !checkoutPrice && !getPrice()?.prevPrice)) {
      return null
    }

    return (
      <div className={s.prevPrice} data-test-id={'prev-price'}>
        {
          showDiscount && discountRate > 0 &&
          <span className={s.discountRate}>
            {`%${discountRate}`}
          </span>
        }
        <span
          className={cx(s.priceText, {[s.lineThrough]: lineThrough})}>{
            formatPrice(campaignEvaluateResult?.evaluateAsPremiumResult?.finalPriceOnSale || campaignEvaluateResult?.evaluateResult?.finalPriceOnSale || getPrice()?.prevPriceValue, true)
          }</span>
      </div>
    )
  }

  const renderDefaultPrice = () => {
    return (
      <>
        <div className={s.defaultPrice}>
          <div className={s.defaultPriceContainer} data-test-id={'default-price'}>
            <div
              className={cx(s.price, {[s.hasPrevPrice]: getPrice().prevPrice})}>
              <span>{getPrice().currentPrice}{' '}{getPrice().currency}</span>
            </div>
            <div className={s.prevPriceContainer}>
              {renderPrevPrice({
                lineThrough: true,
                showDiscount: false
              })}
            </div>
            <div className={s.earnArea}>
              {getResult?.showEarn && renderSeeEarnings()}
            </div>
          </div>
          <div className={s.discountWrapper}>
            {
              product?.prices?.length > 1 &&
              getPrice().discountText
            }
          </div>
        </div>
      </>
    )
  }

  const renderCheckoutPrice = () => {
    return (
      <>
        <div className={s.checkoutPrice} data-test-id={'checkout-price'}>
          <div className={s.textArea}>
            <i className={s.icon}/>
            <span className={s.text}>{'Sepete özel fiyat'}</span>
          </div>
          <div className={s.priceArea}>
            {formatPrice(checkoutPrice, true)}
          </div>
        </div>
        <div className={s.prevPriceContainer}>
          {renderPrevPrice({
            lineThrough: false,
            showDiscount: false
          })}
        </div>
        <div className={s.earnArea}>
          {getResult?.showEarn && renderSeeEarnings()}
        </div>
      </>
    )
  }

  const renderPremiumPrice = () => {
    return (
      <>
        <div className={s.premiumPrice} data-test-id={'premium-price'}>
          <span className={s.text}>{'Premium\'a özel fiyat'}</span>
          <span
            className={s.price}>{formatPrice(premiumPrice, true)}</span>
        </div>
        <div className={s.prevPriceContainer}>
          {renderPrevPrice({
            lineThrough: false,
            showDiscount: false
          })}
        </div>
        <div className={s.earnArea}>
          {getResult?.showEarn && renderSeeEarnings()}
        </div>
      </>
    )
  }

  const renderNonPremiumPrice = () => {
    return (
      <>
        <div className={s.nonPremiumPrice} data-test-id={'non-premium-price'}>
          {
            <>
              <div className={s.defaultPrice}>
                <div className={s.defaultPriceContainer} data-test-id={'default-price'}>
                  <div
                    className={cx(s.price, {[s.hasPrevPrice]: getPrice().prevPrice})}>
                    <span>{getPrice().currentPrice}{' '}{getPrice().currency}</span>
                  </div>
                  <div className={s.prevPriceContainer}>
                    {
                      getPrice()?.prevPrice &&
                      renderPrevPrice({
                        lineThrough: true,
                        showDiscount: false
                      })}
                  </div>
                  <div className={s.earnArea}>
                    {getResult?.showEarn && renderSeeEarnings()}
                  </div>
                </div>
              </div>
            </>
          }
          <span className={s.text}>{'Premium ile '}<b>{formatPrice(premiumPrice, true)}</b></span>
        </div>
      </>
    )
  }

  if (isBot) {
    return (
      <div className={cx(s.root, className)} data-test-id={'price'}>
        {renderDefaultPrice()}
      </div>
    )
  }

  if (!product || isGetCampaignsPdpDashboardPending || loyaltyInfoPending) {
    return <PriceSkeleton/>
  }

  return (
    <div className={cx(s.root, className)} data-test-id={'price'}>
      {getResult?.showPremiumPrice && renderPremiumPrice()}
      {getResult?.showNonPremiumPrice && renderNonPremiumPrice()}
      {getResult?.showCheckoutPrice && renderCheckoutPrice()}
      {getResult?.showDefaultPrice && renderDefaultPrice()}
    </div>
  )
})

export default Price
